import { METRIC_SOURCE } from "@/components/dynamic-data-program-builder/AddMetricSideSheet/metricOptions";

function isTruthyFlag(value) {
  return value === true || value === "true" || value === 1 || value === "1";
}

function nodeHas(node, attribute) {
  return !!attribute && !!node && Object.prototype.hasOwnProperty.call(node, attribute);
}

function nodeValue(node, attribute) {
  if (!nodeHas(node, attribute)) return null;
  return node[attribute];
}

function nodeText(node, attribute) {
  if (!attribute) return "";
  const value = nodeValue(node, attribute);
  if (value == null) return "";
  return String(value);
}

function isColumnVisible(column, node) {
  const attribute = column.visibilityMetricAttribute;
  if (!attribute) return true;
  return isTruthyFlag(nodeValue(node, attribute));
}

function toParticipantMetric(column, node, earnings) {
  const isEarnings = column.metricSource === METRIC_SOURCE.EARNINGS;

  switch (column.componentType) {
    case "PROGRESS_BAR":
      return {
        progress: nodeValue(node, column.informMetricAttribute),
        rangeStartLabel: nodeText(node, column.rangeStartLabelAttribute),
        rangeEndLabel: nodeText(node, column.rangeEndLabelAttribute),
      };
    case "STATUS":
      return {
        met: isTruthyFlag(nodeValue(node, column.informMetricAttribute)),
        metLabel: nodeText(node, column.statusMetLabelAttribute),
        notMetLabel: nodeText(node, column.statusNotMetLabelAttribute),
      };
    default:
      return {
        value: isEarnings
          ? earnings?.accumulatedReward ?? null
          : nodeValue(node, column.informMetricAttribute),
        prefix: nodeText(node, column.prefixLabelAttribute),
        suffix: nodeText(node, column.suffixLabelAttribute),
        label: nodeText(node, column.labelAttribute),
      };
  }
}

function toParticipantColumn(column, node, earnings) {
  return {
    key: column.columnId ?? column.slotIndex,
    componentType: column.componentType,
    isEarnings: column.metricSource === METRIC_SOURCE.EARNINGS,
    metric: toParticipantMetric(column, node, earnings),
  };
}

function toParticipantRow(row, columns, node, earnings) {
  return {
    key: row.rowId,
    columns: columns.map((column) => toParticipantColumn(column, node, earnings)),
  };
}

function sortRows(rows) {
  return (rows || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

function sortColumns(columns) {
  return columns.slice().sort((a, b) => (a.slotIndex ?? 0) - (b.slotIndex ?? 0));
}

/**
 * Maps saved config + the participant Inform node + earnings onto
 * ProgramDetailColumn's metricRows. Hidden / empty slots are dropped so the
 * remaining components reflow — spacers are builder-preview only.
 */
export function toParticipantMetricRows(rows, metricNode, earnings) {
  const node = metricNode ?? {};

  if (metricNode === null) {
    const firstRow = rows?.[0];
    const columns = firstRow?.columns ?? [];
    if (
      columns.length === 1 &&
      columns[0].componentType === "BIG_NUMBER" &&
      columns[0].metricSource === METRIC_SOURCE.EARNINGS
    ) {
      // Show default earnings row if configured
      return {
        rows: [toParticipantRow(firstRow, [columns[0]], node, earnings)],
        showMissingMetricCopy: true,
      };
    }
    return {
      rows: [],
      showMissingMetricCopy: true,
    };
  }

  const metricRows = sortRows(rows).reduce((visibleRows, row) => {
    const columns = sortColumns(
      (row.columns || []).filter(
        (column) => !!column.componentType && isColumnVisible(column, node)
      )
    );

    if (columns.length) {
      visibleRows.push(toParticipantRow(row, columns, node, earnings));
    }
    return visibleRows;
  }, []);
  return {
    rows: metricRows,
    showMissingMetricCopy: false,
  };
}
